import { Play, Pause } from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import './ExpandingVideo.css';

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function ExpandingVideo() {
  const sectionRef = useRef(null);
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const fillRef = useRef(null);
  const thumbRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showBtn, setShowBtn] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const hideTimer = useRef(null);
  const isDragging = useRef(false);
  const rafId = useRef(null);

  // Smooth progress updates via requestAnimationFrame
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    function tick() {
      if (!isDragging.current && video.duration) {
        const pct = (video.currentTime / video.duration) * 100;
        if (fillRef.current) fillRef.current.style.width = `${pct}%`;
        if (thumbRef.current) thumbRef.current.style.left = `${pct}%`;
        setCurrentTime(video.currentTime);
      }
      rafId.current = requestAnimationFrame(tick);
    }

    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
  }, []);

  // Scroll-based expanding effect
  useEffect(() => {
    const section = sectionRef.current;
    const wrapper = wrapperRef.current;
    if (!section || !wrapper) return;

    function onScroll() {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight || 1;
      let scrollProgress = 1 - rect.top / windowHeight;
      scrollProgress = Math.max(0, Math.min(1, scrollProgress));

      if (scrollProgress > 0.3) {
        const widthPercentage = 90 + scrollProgress * 10;
        const finalWidth = Math.min(100, widthPercentage);
        const borderRadius = 16 - scrollProgress * 16;
        wrapper.style.maxWidth = `${finalWidth}%`;
        wrapper.style.borderRadius = `${Math.max(0, borderRadius)}px`;
      } else {
        wrapper.style.maxWidth = '90%';
        wrapper.style.borderRadius = '16px';
      }
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Get duration
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onLoadedMetadata = () => setDuration(video.duration);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    if (video.duration) setDuration(video.duration);
    return () => video.removeEventListener('loadedmetadata', onLoadedMetadata);
  }, []);

  function handleToggle() {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => { });
      setIsPlaying(true);
      setShowBtn(true);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setShowBtn(false), 1200);
    } else {
      video.pause();
      setIsPlaying(false);
      setShowBtn(true);
      clearTimeout(hideTimer.current);
    }
  }

  // Directly update DOM for instant feedback (no React re-render lag)
  const seekToPosition = useCallback((clientX) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar || !video.duration) return;

    const rect = bar.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const pct = (x / rect.width) * 100;

    // Instantly update DOM for smooth visual feedback
    if (fillRef.current) fillRef.current.style.width = `${pct}%`;
    if (thumbRef.current) thumbRef.current.style.left = `${pct}%`;

    video.currentTime = (pct / 100) * video.duration;
    setCurrentTime(video.currentTime);
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    isDragging.current = true;
    seekToPosition(e.clientX);

    // Add dragging class for visual cue
    progressRef.current?.classList.add('expVideo__progressBar--dragging');

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      seekToPosition(moveEvent.clientX);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      progressRef.current?.classList.remove('expVideo__progressBar--dragging');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [seekToPosition]);

  return (
    <section id="video-section" ref={sectionRef} className="expVideo">
      <div ref={wrapperRef} className="expVideo__wrapper" onClick={handleToggle} style={{ cursor: 'pointer' }}>
        <div className="expVideo__overlay" />

        <video
          ref={videoRef}
          className="expVideo__video"
          autoPlay
          loop
          muted
          playsInline
        >
          <source
            src="/revitrone.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        <div className="expVideo__caption">
          <h3 className="expVideo__title">See Revitrone in Action</h3>
          <p className="expVideo__subtitle">
            AI-powered code generation for Revit &amp; Rhino · Smart dashboards &amp; templates · Real-time team collaboration
          </p>
        </div>

        <div className={`expVideo__playBtn ${showBtn ? 'expVideo__playBtn--visible' : ''} ${!isPlaying ? 'expVideo__playBtn--paused' : ''}`}>
          {isPlaying ? (
            <Pause className="expVideo__playIcon" aria-hidden="true" />
          ) : (
            <Play className="expVideo__playIcon" aria-hidden="true" />
          )}
        </div>

        {/* YouTube-style progress bar */}
        <div className="expVideo__controls" onClick={(e) => e.stopPropagation()}>
          <span className="expVideo__time">
            {formatTime(currentTime)}
          </span>
          <div
            className="expVideo__progressBar"
            ref={progressRef}
            onMouseDown={handleMouseDown}
          >
            <div className="expVideo__progressTrack">
              <div
                className="expVideo__progressFill"
                ref={fillRef}
              />
              <div
                className="expVideo__progressThumb"
                ref={thumbRef}
              />
            </div>
          </div>
          <span className="expVideo__time">
            {formatTime(duration)}
          </span>
        </div>
      </div>
    </section>
  );
}
